import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import type { StateInfo } from "@/lib/states";
import { MONTH_NAMES, MONTH_SHORT, inPeakCount, seasonMonths } from "@/lib/season";
import { SOURCES } from "@/lib/sources";
import { cn } from "@/lib/utils";

const SCOPE_NOTE: Record<StateInfo["seasonSource"]["scope"], string> = {
  state: "",
  regional: "regional figure",
  national: "national guide",
};

/**
 * A phenology chart, the way a field guide prints flight seasons: one row per state, a
 * strip of twelve months with the peak window filled in, and the source for those months
 * at the end of the row. Built as a list rather than a table so a screen reader hears
 * "Georgia, March to October, source Georgia Department of Public Health" instead of
 * twelve empty cells.
 *
 * The current month is marked only after the page loads, from the reader's own clock:
 * the page is cached, and a month baked into the HTML would be wrong next month.
 */
export function SeasonChart({ states }: { states: StateInfo[] }) {
  const [month, setMonth] = useState<number | null>(null);
  useEffect(() => setMonth(new Date().getMonth()), []);

  const charted = states
    .filter((s) => s.risk !== "low")
    .sort((a, b) => a.name.localeCompare(b.name));
  const absent = states
    .filter((s) => s.risk === "low")
    .sort((a, b) => a.name.localeCompare(b.name));
  const inSeason =
    month === null
      ? 0
      : inPeakCount(
          charted.map((s) => s.peakSeason),
          new Date(),
        );

  const grid =
    "grid grid-cols-[7.5rem_minmax(0,1fr)] items-center gap-x-3 sm:grid-cols-[9rem_minmax(0,1fr)_8.5rem] lg:grid-cols-[9rem_minmax(0,1fr)_8.5rem_11rem]";

  return (
    <div>
      <p className="text-[0.9375rem] leading-relaxed text-foreground" aria-live="polite">
        {month === null ? (
          <>Peak months for the {charted.length} states where Lone Star ticks are found.</>
        ) : (
          <>
            <span className="font-semibold">{MONTH_NAMES[month]}:</span> peak season is under way in{" "}
            {inSeason} of the {charted.length} states where Lone Star ticks are found or spreading.
          </>
        )}
      </p>

      <div aria-hidden className={cn(grid, "mt-5 border-b border-rule pb-1.5")}>
        <span className="label-caps text-muted-foreground">State</span>
        <span className="grid grid-cols-12 text-center text-[0.6875rem] text-muted-foreground">
          {MONTH_SHORT.map((m, i) => (
            <span key={m} className={cn(i === month && "font-semibold text-foreground")}>
              {m[0]}
            </span>
          ))}
        </span>
        <span className="label-caps hidden text-muted-foreground sm:block">Peak</span>
        <span className="label-caps hidden text-muted-foreground lg:block">Source</span>
      </div>

      <ol>
        {charted.map((s) => {
          const months = seasonMonths(s.peakSeason);
          const ref = SOURCES[s.seasonSource.sourceId];
          const note = SCOPE_NOTE[s.seasonSource.scope];
          return (
            <li key={s.slug} className={cn(grid, "border-b border-border py-1.5")}>
              <Link
                to="/states/$state"
                params={{ state: s.slug }}
                className="link truncate text-[0.9375rem]"
              >
                {s.name}
              </Link>
              <span aria-hidden className="grid h-4 grid-cols-12 gap-px">
                {months.map((a, i) => (
                  <span
                    key={i}
                    className={cn(
                      "block h-full [print-color-adjust:exact]",
                      i === month && "outline outline-[1.5px] outline-foreground",
                    )}
                    style={{
                      backgroundColor:
                        a === "peak"
                          ? "var(--rust)"
                          : a === "active"
                            ? "color-mix(in oklab, var(--rust) 30%, transparent)"
                            : "color-mix(in oklab, var(--foreground) 7%, transparent)",
                    }}
                  />
                ))}
              </span>
              <span className="col-start-2 text-xs text-muted-foreground sm:col-start-auto sm:text-sm sm:text-foreground">
                <span className="sr-only">Peak season: </span>
                {s.peakSeason}
              </span>
              <span className="col-start-2 text-xs leading-snug text-muted-foreground lg:col-start-auto">
                {ref ? (
                  <>
                    <span className="sr-only">Source: </span>
                    <a
                      href={ref.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link print-no-url"
                      title={ref.title}
                    >
                      {ref.short ?? ref.org}
                      <span className="sr-only"> (opens in a new tab)</span>
                    </a>
                    {note && <span>, {note}</span>}
                  </>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="h-3 w-3 bg-rust [print-color-adjust:exact]" /> Peak months
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className="h-3 w-3 [print-color-adjust:exact]"
            style={{ backgroundColor: "color-mix(in oklab, var(--rust) 30%, transparent)" }}
          />{" "}
          Active all year, outside the peak
        </span>
        {month !== null && (
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="h-3 w-3 outline outline-[1.5px] outline-foreground" /> This
            month
          </span>
        )}
      </div>

      {absent.length > 0 && (
        <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
          <span className="font-semibold text-foreground">Not established:</span>{" "}
          {absent.map((s, i) => (
            <span key={s.slug}>
              <Link to="/states/$state" params={{ state: s.slug }} className="link">
                {s.name}
              </Link>
              {i < absent.length - 1 ? ", " : ". "}
            </span>
          ))}
          Other ticks live in these states, and CDC says ticks in general are most active from April
          to September.
        </p>
      )}
    </div>
  );
}
